import { API } from "../../api";

function transformListResponse(responseData) {
  const data = responseData?.data;
  return {
    items: data?.items || [],
    pagination: data?.pagination || {},
  };
}

export const staffInvestmentsApi = API.injectEndpoints({
  endpoints: (builder) => ({
    listStaffOpportunities: builder.query({
      query: (params = {}) => ({
        url: "/cooperatives/investments/opportunities",
        method: "GET",
        params,
      }),
      transformResponse: transformListResponse,
      providesTags: (result) =>
        result?.items?.length
          ? [
              ...result.items.map((item) => ({ type: "StaffOpportunities", id: item.id })),
              { type: "StaffOpportunities", id: "LIST" },
            ]
          : [{ type: "StaffOpportunities", id: "LIST" }],
    }),
    getStaffOpportunity: builder.query({
      query: (id) => ({
        url: `/cooperatives/investments/opportunities/${id}`,
        method: "GET",
      }),
      transformResponse: (responseData) => responseData?.data,
      providesTags: (_r, _e, id) => [{ type: "StaffOpportunities", id }],
    }),
    getStaffOpportunityBySlug: builder.query({
      query: (slug) => ({
        url: `/cooperatives/investments/opportunities/by-slug/${slug}`,
        method: "GET",
      }),
      transformResponse: (responseData) => responseData?.data,
      providesTags: (_r, _e, slug) => [{ type: "StaffOpportunities", id: `slug-${slug}` }],
    }),
    createStaffOpportunity: builder.mutation({
      query: (body) => ({
        url: "/cooperatives/investments/opportunities",
        method: "POST",
        body,
      }),
      transformResponse: (responseData) => responseData?.data,
      invalidatesTags: [{ type: "StaffOpportunities", id: "LIST" }, { type: "StaffInvestmentDashboard", id: "SUMMARY" }],
    }),
    updateStaffOpportunity: builder.mutation({
      query: ({ id, body }) => ({
        url: `/cooperatives/investments/opportunities/${id}`,
        method: "PATCH",
        body,
      }),
      transformResponse: (responseData) => responseData?.data,
      invalidatesTags: (_r, _e, { id }) => [
        { type: "StaffOpportunities", id },
        { type: "StaffOpportunities", id: "LIST" },
      ],
    }),
    staffOpportunityLifecycle: builder.mutation({
      query: ({ id, action }) => ({
        url: `/cooperatives/investments/opportunities/${id}/${action}`,
        method: "POST",
      }),
      transformResponse: (responseData) => responseData?.data,
      invalidatesTags: (_r, _e, { id }) => [
        { type: "StaffOpportunities", id },
        { type: "StaffOpportunities", id: "LIST" },
        { type: "StaffInvestmentDashboard", id: "SUMMARY" },
      ],
    }),
    addStaffOpportunityUpdate: builder.mutation({
      query: ({ id, body }) => ({
        url: `/cooperatives/investments/opportunities/${id}/updates`,
        method: "POST",
        body,
      }),
      transformResponse: (responseData) => responseData?.data,
      invalidatesTags: (_r, _e, { id }) => [{ type: "StaffOpportunities", id }],
    }),
    addStaffOpportunityDocument: builder.mutation({
      query: ({ id, body }) => ({
        url: `/cooperatives/investments/opportunities/${id}/documents`,
        method: "POST",
        body,
      }),
      transformResponse: (responseData) => responseData?.data,
      invalidatesTags: (_r, _e, { id }) => [{ type: "StaffOpportunities", id }],
    }),
    deleteStaffOpportunityUpdate: builder.mutation({
      query: ({ id, updateId }) => ({
        url: `/cooperatives/investments/opportunities/${id}/updates/${updateId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, { id }) => [{ type: "StaffOpportunities", id }],
    }),
    deleteStaffOpportunityDocument: builder.mutation({
      query: ({ id, documentId }) => ({
        url: `/cooperatives/investments/opportunities/${id}/documents/${documentId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, { id }) => [{ type: "StaffOpportunities", id }],
    }),
    listStaffInvestmentParticipants: builder.query({
      query: (params = {}) => ({
        url: "/cooperatives/investments/participants",
        method: "GET",
        params,
      }),
      transformResponse: transformListResponse,
      providesTags: [{ type: "StaffInvestmentParticipants", id: "LIST" }],
    }),
    listStaffInvestmentRequests: builder.query({
      query: (params = {}) => ({
        url: "/cooperatives/investments/requests",
        method: "GET",
        params,
      }),
      transformResponse: transformListResponse,
      providesTags: (result) =>
        result?.items?.length
          ? [
              ...result.items.map((item) => ({ type: "StaffInvestmentRequests", id: item.id })),
              { type: "StaffInvestmentRequests", id: "LIST" },
            ]
          : [{ type: "StaffInvestmentRequests", id: "LIST" }],
    }),
    getStaffInvestmentRequest: builder.query({
      query: (id) => ({
        url: `/cooperatives/investments/requests/${encodeURIComponent(id)}`,
        method: "GET",
      }),
      transformResponse: (responseData) => responseData?.data,
      providesTags: (_r, _e, id) => [{ type: "StaffInvestmentRequests", id }],
    }),
    staffInvestmentRequestAction: builder.mutation({
      query: ({ id, action, body }) => ({
        url: `/cooperatives/investments/requests/${encodeURIComponent(id)}/${action}`,
        method: "POST",
        body,
      }),
      transformResponse: (responseData) => responseData?.data,
      invalidatesTags: (_r, _e, { id }) => [
        { type: "StaffInvestmentRequests", id },
        { type: "StaffInvestmentRequests", id: "LIST" },
        { type: "StaffInvestmentDashboard", id: "SUMMARY" },
        { type: "StaffInvestmentParticipants", id: "LIST" },
        { type: "StaffOpportunities", id: "LIST" },
      ],
    }),
    getStaffInvestmentDashboardSummary: builder.query({
      query: () => ({
        url: "/cooperatives/investments/dashboard/summary",
        method: "GET",
      }),
      transformResponse: (responseData) => responseData?.data,
      providesTags: [{ type: "StaffInvestmentDashboard", id: "SUMMARY" }],
    }),
  }),
});

export const {
  useListStaffOpportunitiesQuery,
  useGetStaffOpportunityQuery,
  useGetStaffOpportunityBySlugQuery,
  useCreateStaffOpportunityMutation,
  useUpdateStaffOpportunityMutation,
  useStaffOpportunityLifecycleMutation,
  useAddStaffOpportunityUpdateMutation,
  useAddStaffOpportunityDocumentMutation,
  useDeleteStaffOpportunityUpdateMutation,
  useDeleteStaffOpportunityDocumentMutation,
  useListStaffInvestmentParticipantsQuery,
  useListStaffInvestmentRequestsQuery,
  useGetStaffInvestmentRequestQuery,
  useStaffInvestmentRequestActionMutation,
  useGetStaffInvestmentDashboardSummaryQuery,
} = staffInvestmentsApi;
